/**
 * importedDocHiddenPartsSlice.ts — which parts of an imported document a
 * reviewer has marked as irrelevant: table columns, and whole top-level blocks
 * (paragraphs, images, lists, tables…).
 *
 * Deleting is really HIDING: nothing is removed from the document's
 * contentText, so every choice is revertible and the underlying assessments
 * survive. Edits accumulate in `pendingChanges` and only reach the server when
 * the user presses Save, matching the departments / document-links slices (and
 * the requirement in feature.md that Save is required to persist).
 *
 * Visibility is a property of the DOCUMENT, not of the viewer: two reviewers
 * open the same document and see the same parts.
 *
 * Both kinds share one store, one endpoint and one batch op, because they are
 * the same thing to the backend — a (document, element, ordinal, index) tuple to
 * filter out at render time — and because a reviewer's Save has to commit them
 * atomically.
 */

import { createAsyncThunk, createEntityAdapter, createSelector, createSlice, EntityState, PayloadAction } from "@reduxjs/toolkit"
import { AppDataState, AppThunkAPIType, unknownError } from "../../../app/appTypes"
// Type-only: importing the `store` VALUE here would close a cycle (store.ts
// imports this reducer), leaving the reducer undefined whenever this module is
// loaded first. The save reads state via the thunk's getState instead.
import type { RootState } from "../../../app/store"
import { getWithAuth, postWithAuth } from "../../../http"
import { HiddenIndexMap, partId } from "./ast/tableVisibility"

export type Op = 'ADD' | 'DELETE'

/** COLUMN addresses one column inside a table; BLOCK addresses a whole
 *  top-level node (paragraph, image, list, the table itself). */
export type HiddenPartKind = 'COLUMN' | 'BLOCK'

export interface HiddenPart {
    id: number
    documentId: number
    importedDocumentElementId: number
    kind: HiddenPartKind
    /** Which part of the element this addresses — for COLUMN, which <table>;
     *  for BLOCK, which top-level node. 0 unless the element expands into
     *  several (see partId in ast/tableVisibility). */
    ordinal: number
    /** COLUMN: 0-based LOGICAL column index (colspan/rowspan resolved), not cell
     *  position. BLOCK: unused, always 0. */
    partIndex: number
}

export interface HiddenPartOp {
    operation: Op
    id: number
    importedDocumentElementId: number
    kind: HiddenPartKind
    ordinal: number
    partIndex: number
}

/**
 * Identity of one hidden part, used as the entity id and the pending key.
 *
 * `partId` is the AST-side identity ("42" or "42#1"): it may contain '#' but
 * never ':', so the key parses unambiguously by first and last colon.
 */
export const partKey = (kind: HiddenPartKind, id: string, index = 0) => `${kind}:${id}:${index}`

/** Inverse of `partKey`. */
export function parsePartKey(key: string): { kind: HiddenPartKind; id: string; index: number } {
    const first = key.indexOf(":")
    const last = key.lastIndexOf(":")
    return {
        kind: key.slice(0, first) as HiddenPartKind,
        id: key.slice(first + 1, last),
        index: parseInt(key.slice(last + 1), 10),
    }
}

/** The AST-side identity of the table / block a stored part points at. */
const entityPartId = (p: HiddenPart) => partId(String(p.importedDocumentElementId), p.ordinal)

/** Split "42#1" back into the element id and ordinal the DTO carries. */
function splitPartId(id: string): { elementId: number; ordinal: number } {
    const [elementPart, ordinalPart] = id.split("#")
    return {
        elementId: parseInt(elementPart, 10),
        ordinal: ordinalPart ? parseInt(ordinalPart, 10) : 0,
    }
}

export const loadAllHiddenParts = createAsyncThunk<
    HiddenPart[],
    { docId: number },
    AppThunkAPIType
>("imported-documents/hidden-parts/load-all",
    async ({ docId }, { dispatch, rejectWithValue }) => {
        const result = await dispatch(getWithAuth({ url: `api/imported-documents/${docId}/hidden-parts` }));
        const { payload } = result;

        if (getWithAuth.fulfilled.match(result)) {
            return payload as HiddenPart[];
        }

        return rejectWithValue(payload ?? { kind: "unknown" });
    }
);

export const updateHiddenParts = createAsyncThunk<
    HiddenPart[],
    { docId: number, items: HiddenPartOp[] },
    AppThunkAPIType
>("imported-documents/hidden-parts/batch-update", async ({ docId, items }, { dispatch, rejectWithValue }) => {
    const result = await dispatch(
        postWithAuth({
            url: `api/imported-documents/${docId}/hidden-parts/batch`,
            payload: {
                items: items,
            },
        })
    );

    const { payload } = result;

    if (postWithAuth.fulfilled.match(result)) {
        return payload as HiddenPart[];
    }

    return rejectWithValue(payload ?? { kind: "unknown" });
});

const adapter = createEntityAdapter<HiddenPart>({
    selectId: p => partKey(p.kind, entityPartId(p), p.partIndex),
})

interface ImportedDocHiddenPartsState {
    docId: number | null
    hidden: EntityState<HiddenPart>
    loaded: AppDataState
    /** partKey → desired visibility. true = hidden, false = restored. */
    pendingChanges: Record<string, boolean>
}

const initialState: ImportedDocHiddenPartsState = {
    docId: null,
    hidden: adapter.getInitialState(),
    loaded: { type: 'empty' },
    pendingChanges: {},
}

const importedDocHiddenPartsSlice = createSlice({
    name: 'importedDocHiddenParts',
    initialState,
    reducers: {
        toggleHiddenPart(state, action: PayloadAction<{ kind: HiddenPartKind; id: string; index?: number }>) {
            const { kind, id, index = 0 } = action.payload
            const key = partKey(kind, id, index)
            const committed = state.hidden.entities[key] != null
            const current = key in state.pendingChanges ? state.pendingChanges[key] : committed
            const next = !current
            // Toggling back to the committed value is not a change — drop the
            // entry so Save disables again instead of posting a no-op batch.
            if (next === committed) delete state.pendingChanges[key]
            else state.pendingChanges[key] = next
        },
        clearPendingHiddenParts(state) {
            state.pendingChanges = {}
        },
    },
    extraReducers: builder => {
        builder.addCase(loadAllHiddenParts.pending, (state, action) => {
            state.docId = action.meta.arg.docId
            state.loaded = {
                type: 'loading',
                requestId: action.meta.requestId,
            }
        })
        builder.addCase(loadAllHiddenParts.fulfilled, (state, action) => {
            state.docId = action.meta.arg.docId
            state.loaded = { type: 'loaded' }
            adapter.setAll(state.hidden, action.payload)
            // A fresh load is a new baseline; stale pending edits would re-apply
            // against parts that may no longer exist.
            state.pendingChanges = {}
        })
        builder.addCase(loadAllHiddenParts.rejected, (state, action) => {
            state.loaded = {
                type: 'error',
                error: action.payload ?? unknownError(),
            }
        })
        builder.addCase(updateHiddenParts.fulfilled, (state, action) => {
            adapter.setAll(state.hidden, action.payload)
            state.pendingChanges = {}
        })
    },
})

export const { toggleHiddenPart, clearPendingHiddenParts } = importedDocHiddenPartsSlice.actions

/**
 * Turn the pending edits into a batch of ADD / DELETE ops and post them.
 * A no-op when nothing is pending, so it is safe to call on every Save.
 */
export const saveHiddenParts = createAsyncThunk<
    void,
    number,
    AppThunkAPIType
>("imported-documents/hidden-parts/save", async (docId, { dispatch, getState }) => {
    const { pendingChanges, hidden } = getState().importedDocHiddenParts
    const ops: HiddenPartOp[] = []

    for (const [key, shouldHide] of Object.entries(pendingChanges)) {
        const existing = hidden.entities[key]
        const { kind, id, index } = parsePartKey(key)

        if (shouldHide) {
            if (existing) continue // already hidden server-side
            const { elementId, ordinal } = splitPartId(id)
            ops.push({
                operation: 'ADD',
                id: 0,
                importedDocumentElementId: elementId,
                kind,
                ordinal,
                partIndex: index,
            })
        } else if (existing) {
            ops.push({
                operation: 'DELETE',
                id: existing.id,
                importedDocumentElementId: existing.importedDocumentElementId,
                kind: existing.kind,
                ordinal: existing.ordinal,
                partIndex: existing.partIndex,
            })
        }
    }

    if (ops.length > 0) {
        await dispatch(updateHiddenParts({ docId, items: ops })).unwrap()
    }
})

const selectState = (state: RootState) => state.importedDocHiddenParts

export const selectHiddenPartsLoaded = (state: RootState) => state.importedDocHiddenParts.loaded

export const selectHasPendingHiddenParts = (state: RootState) =>
    Object.keys(state.importedDocHiddenParts.pendingChanges).length > 0

/** What the renderer and the AST transform consume. */
export interface HiddenPartsView {
    columns: HiddenIndexMap
    blockIds: Set<string>
}

/**
 * Committed state overlaid with the unsaved edits — the shape
 * `applyTableVisibility` and `StaticDocument` consume. What the reviewer sees
 * always reflects their pending choices, which is why the transform reads this
 * and not the committed entities.
 *
 * Memoized: it builds fresh objects, and the result feeds a `useMemo` that
 * re-runs the AST transform over the whole document. A plain selector would be
 * reference-unequal on every store change and re-parse the document on each
 * keystroke elsewhere in the form.
 */
export const selectHiddenParts = createSelector(
    [(state: RootState) => selectState(state).hidden.entities,
     (state: RootState) => selectState(state).pendingChanges],
    (entities, pendingChanges): HiddenPartsView => {
        const columns: Record<string, Set<number>> = {}
        const blockIds = new Set<string>()

        const apply = (kind: HiddenPartKind, id: string, index: number, hide: boolean) => {
            if (kind === 'BLOCK') {
                if (hide) blockIds.add(id)
                else blockIds.delete(id)
                return
            }
            if (hide) (columns[id] ??= new Set()).add(index)
            else columns[id]?.delete(index)
        }

        for (const p of Object.values(entities)) {
            if (p) apply(p.kind, entityPartId(p), p.partIndex, true)
        }
        for (const [key, shouldHide] of Object.entries(pendingChanges)) {
            const { kind, id, index } = parsePartKey(key)
            apply(kind, id, index, shouldHide)
        }

        const freeze = (map: Record<string, Set<number>>): HiddenIndexMap => {
            const out: HiddenIndexMap = {}
            for (const [id, set] of Object.entries(map)) {
                if (set.size > 0) out[id] = [...set].sort((a, b) => a - b)
            }
            return out
        }

        return { columns: freeze(columns), blockIds }
    },
)

export default importedDocHiddenPartsSlice.reducer
